"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"

export type Locale = "en" | "zh"

// Translation dictionary
const translations = {
  // Navigation
  nav: {
    work: { en: "Work", zh: "作品" },
    approach: { en: "Approach", zh: "方法" },
    about: { en: "About", zh: "關於" },
    letsTalk: { en: "Let's Talk", zh: "聊聊" },
  },
  // Hero section
  hero: {
    headline1: { en: "You've built something", zh: "你創造了" },
    headline2: { en: "extraordinary.", zh: "不凡的事業" },
    subheadline: { en: "Help the world see what you see.", zh: "讓世界看見你心中的願景" },
    annotation: { en: "← that's my passion", zh: "這是我的熱忱" },
    tagline: { en: "Brand Engineering × Creative Intelligence", zh: "品牌工程 × 創意智能" },
    years: { en: "years.", zh: "年" },
    projects: { en: "projects.", zh: "個專案" },
    mission: { en: "mission.", zh: "個使命" },
  },
  // Trust strip
  trust: {
    tagline: {
      en: "Trusted by technology leaders who need to be understood",
      zh: "科技領袖的信任，源自真正的理解",
    },
  },
  // Translation types / Gap section
  gap: {
    sectionLabel: { en: "The Gap", zh: "鴻溝" },
    headline: {
      words: { en: ["Where", "Innovation", "Gets", "Lost"], zh: ["創新", "為何", "無法", "傳達"] },
    },
    subtitle1: {
      en: "You know what you've built. But does your market?",
      zh: "你清楚自己做出了什麼。但市場呢？",
    },
    subtitle2: {
      en: "The distance between your technology and their understanding is where opportunity dies.",
      zh: "技術與理解之間的落差，正在消耗你的市場機會。",
    },
    cards: {
      complexity: {
        without: { en: "Complexity", zh: "複雜" },
        with: { en: "Story", zh: "故事" },
        description: {
          en: "Brilliance that can't be told can't be sold.",
          zh: "說不出的精彩，賣不了的價值。",
        },
      },
      vision: {
        without: { en: "Vision", zh: "願景" },
        with: { en: "Strategy", zh: "策略" },
        description: {
          en: "Dreams without direction are just daydreams.",
          zh: "沒有方向的夢想，只是白日夢。",
        },
      },
      tech: {
        without: { en: "Technology", zh: "技術" },
        with: { en: "Humanity", zh: "人性" },
        description: {
          en: "Innovation that can't connect stays invisible.",
          zh: "無法連結的創新，終將隱沒無形。",
        },
      },
    },
    bridge: { en: "I build the bridge.", zh: "我來搭這座橋。" },
    bridgeSub: {
      en: "Translating your innovation into market impact.",
      zh: "將你的創新轉譯為市場影響力。",
    },
  },
  // Work section
  work: {
    sectionLabel: { en: "The Work", zh: "作品集" },
    headline: { en: "Bridges Built", zh: "搭過的橋" },
    bottomDesc: {
      en: "Strategic projects for technology leaders across semiconductors, manufacturing, healthcare, and emerging tech.",
      zh: "橫跨半導體、製造、醫療與新興科技，為技術領袖設計的策略專案。",
    },
    industry: { en: "Industry", zh: "產業" },
    services: { en: "Services", zh: "服務" },
    gapBridged: { en: "Gap Bridged", zh: "跨越的鴻溝" },
    theChallenge: { en: "The Challenge", zh: "挑戰" },
    theApproach: { en: "The Approach", zh: "方法" },
    theImpact: { en: "The Impact", zh: "成果" },
    viewFullCase: { en: "View Full Case", zh: "看完整案例" },
    comingSoon: { en: "Coming Soon", zh: "即將推出" },
    close: { en: "Close", zh: "關閉" },
  },
  // Method / Approach section
  method: {
    sectionLabel: { en: "The Approach", zh: "方法論" },
    quote1: {
      en: "\"Renaissance masters didn't separate art from science.",
      zh: "「文藝復興的大師，從不把藝術和科學分開。",
    },
    quote2: { en: 'Neither do I."', zh: "我也致力於探索創意與科技的交融。」" },
    phases: {
      discover: {
        name: { en: "DISCOVER", zh: "探索" },
        subtitle: { en: "Immersion & Research", zh: "沉浸與研究" },
        description: {
          en: "Understanding the full picture before forming a point of view.",
          zh: "先看清全貌，再形成觀點。",
        },
        services: {
          en: [
            "Market & Competitive Analysis",
            "Stakeholder Interviews",
            "Consumer Research & Segmentation",
            "Cultural & Trend Insights",
            "Brand Audit & Diagnostics",
          ],
          zh: [
            "市場與競爭分析",
            "關鍵人物訪談",
            "消費者研究與區隔",
            "文化與趨勢洞察",
            "品牌健檢與診斷",
          ],
        },
      },
      define: {
        name: { en: "DEFINE", zh: "定義" },
        subtitle: { en: "Strategy & Positioning", zh: "策略與定位" },
        description: {
          en: "Synthesizing insights into clear strategic direction.",
          zh: "把洞察收斂為清晰的策略方向。",
        },
        services: {
          en: [
            "Brand Positioning & Differentiation",
            "Brand Architecture & Portfolio",
            "Purpose, Vision & Values",
            "Value Proposition Development",
            "Brand Narrative & Story",
          ],
          zh: [
            "品牌定位與差異化",
            "品牌架構與組合策略",
            "使命、願景與核心價值",
            "價值主張發展",
            "品牌敘事與故事",
          ],
        },
      },
      design: {
        name: { en: "DESIGN", zh: "設計" },
        subtitle: { en: "Expression & System", zh: "表達與系統" },
        description: {
          en: "Translating strategy into tangible brand assets.",
          zh: "讓策略落地，化為看得見的品牌資產。",
        },
        services: {
          en: [
            "Naming & Nomenclature",
            "Visual Identity System",
            "Verbal Identity & Tone of Voice",
            "Messaging Framework",
            "Brand Guidelines",
            "Digital & Website Experience",
          ],
          zh: [
            "品牌命名與名稱體系",
            "視覺識別系統",
            "語言風格與品牌語調",
            "訊息架構",
            "品牌規範",
            "數位與網站體驗",
          ],
        },
      },
      deliver: {
        name: { en: "DELIVER", zh: "落地" },
        subtitle: { en: "Activation & Launch", zh: "啟動與上市" },
        description: {
          en: "From guidelines to market. Making strategy real.",
          zh: "從規範到市場，讓策略真正發生。",
        },
        services: {
          en: [
            "Go-to-Market Strategy",
            "Launch Planning & Execution",
            "Campaign Development",
            "Employer Branding",
            "Internal Engagement",
            "Training & Enablement",
          ],
          zh: [
            "市場進入策略",
            "上市規劃與執行",
            "行銷活動開發",
            "雇主品牌",
            "內部溝通",
            "培訓與建能",
          ],
        },
      },
    },
    footer: {
      en: "Every project is different. This is the structure that adapts.",
      zh: "每個專案都不同，這套架構會隨需求靈活調整。",
    },
    cta: { en: "Have a challenge in mind?", zh: "心中有想解決的挑戰？" },
  },
  // About section
  about: {
    sectionLabel: { en: "About", zh: "關於我" },
    headline: { en: "The Builder Who Became a Strategist", zh: "從工程師到策略家，同一種思維" },
    statYears: { en: "Years", zh: "年" },
    statProjects: { en: "Projects", zh: "專案" },
    statCompanies: { en: "Companies", zh: "企業" },
    bio1: {
      en: "I started in engineering — process engineering for nuclear power plants and EV infrastructure. I learned that the most sophisticated systems fail if people don't understand them.",
      zh: "我從工程起步，核電廠與電動車基礎設施的製程工程。我體認到，再精密的系統，人理解不了，就發揮不了價值。",
    },
    bio2: {
      en: "That realization led me to brand strategy. For the past 15 years, I've helped technology companies bridge the gap between what they build and what the world understands.",
      zh: "這個體悟引領我走向品牌策略。過去 15 年，我幫助科技公司跨越「做得出來」與「說得明白」之間的鴻溝。",
    },
    bio3: {
      en: "Today, as Creative Intelligence Officer at RedPeak — part of the kyu Collective network — I lead strategic engagements for technology leaders across semiconductors, edge computing, and precision instruments.",
      zh: "現在，我在 RedPeak 擔任創意智能長，隸屬 kyu Collective 體系。專注服務半導體、邊緣運算與精密量測領域的科技領袖，主導品牌策略專案。",
    },
    bio4: {
      en: "My approach combines an engineer's rigor with a strategist's intuition. Because the best brands aren't just designed. They're engineered.",
      zh: "我的方法，結合工程師的嚴謹與策略家的直覺。因為最好的品牌，不只是設計出來的，是工程化出來的。",
    },
    currentlyAt: { en: "Currently at", zh: "目前任職於" },
    partOf: { en: ", part of", zh: "，隸屬於" },
    beyond: {
      en: "Beyond strategy: Handpan musician. Photographer. Lifelong learner.",
      zh: "策略之外：手碟演奏者。攝影師。終身學習者。",
    },
  },
  // Subscribe section
  subscribe: {
    sectionLabel: { en: "The Signal", zh: "訊號" },
    headline: { en: "Get the signal, not the noise.", zh: "接收訊號，而非雜訊。" },
    subtitle: {
      en: "Occasional insights on brand strategy, creative intelligence, and making the complex clear.",
      zh: "不定期分享品牌策略、創意智能，以及如何化繁為簡的洞察。",
    },
    namePlaceholder: { en: "Your name", zh: "你的姓名" },
    emailPlaceholder: { en: "your@email.com", zh: "your@email.com" },
    subscribe: { en: "Subscribe", zh: "訂閱" },
    subscribing: { en: "Subscribing...", zh: "訂閱中..." },
    privacy: {
      en: "No spam. Unsubscribe anytime.",
      zh: "不浪費你的時間。隨時可以退訂。",
    },
  },
  // Contact section
  contact: {
    sectionLabel: { en: "Connect", zh: "聯繫" },
    headline1: { en: "If your innovation needs a voice,", zh: "如果你的創新還在找它的聲音，" },
    headline2: { en: "let's find it together.", zh: "我們一起找到它。" },
    letsTalk: { en: "Let's Talk", zh: "開始對話" },
    getInTouch: { en: "Get in Touch", zh: "取得聯繫" },
    formSubtitle: {
      en: "Fill in your details and I'll get back to you soon.",
      zh: "留下你的資料，我會盡快回覆。",
    },
    nameLabel: { en: "Name", zh: "姓名" },
    namePlaceholder: { en: "Your name", zh: "你的名字" },
    emailLabel: { en: "Email", zh: "電子郵件" },
    emailPlaceholder: { en: "your.email@example.com", zh: "your.email@example.com" },
    messageLabel: { en: "Message", zh: "訊息" },
    messagePlaceholder: { en: "Tell me about your project...", zh: "聊聊你的專案..." },
    sendMessage: { en: "Send Message", zh: "送出訊息" },
    sending: { en: "Sending...", zh: "送出中..." },
  },
  // Language toggle
  langToggle: {
    switchTo: { en: "切換至中文", zh: "Switch to English" },
  },
} as const

// Type helper
type TranslationValue = string | string[] | { [key: string]: TranslationValue }

// Context
interface LanguageContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (path: string) => string
  tArray: (path: string) => string[]
}

const LanguageContext = createContext<LanguageContextType | null>(null)

// Helper to get nested value from translations
function getNestedValue(obj: any, path: string, locale: Locale): any {
  const keys = path.split(".")
  let current = obj
  for (const key of keys) {
    if (current === undefined || current === null) return path
    current = current[key]
  }
  if (current === undefined || current === null) return path
  // If the value has locale keys, return the locale value
  if (typeof current === "object" && locale in current) {
    return current[locale]
  }
  return current
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("en")

  useEffect(() => {
    const saved = localStorage.getItem("raychard-locale") as Locale | null
    if (saved && (saved === "en" || saved === "zh")) {
      setLocaleState(saved)
    }
  }, [])

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale)
    localStorage.setItem("raychard-locale", newLocale)
  }

  const t = (path: string): string => {
    const value = getNestedValue(translations, path, locale)
    if (typeof value === "string") return value
    return path
  }

  const tArray = (path: string): string[] => {
    const value = getNestedValue(translations, path, locale)
    if (Array.isArray(value)) return value
    return [path]
  }

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t, tArray }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}
